import CloseIcon from "@mui/icons-material/Close";
import { Button, Dialog, DialogActions, IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";

import { useSnackbar } from "notistack";
import { useAiQualityTool } from "../../../context/AiQualityToolContext";
import { AnalyzeIndicator } from "../../cargo/CargoAI/AnalyzeIndicator";
import { FormGrid } from "../../cargo/CargoAI/FormGrid";
import {
	useCheckJobStatusQuery,
	useUploadCargoScreenShotMutation,
} from "../../cargo/cargoApiSlice";
import { useGetCurrentUserQuery } from "../../user/userApiSlice";
import { EmailViewer } from "./email-viewer";

import "../../../mailViewer.css";
import { prepareCargosData } from "../../../utils/aiUtils";

const modalSizeDictionary = {
	0: "xl",
	1: "sm",
	2: "lg",
};

export const ContentAnalyzeDialog = ({
	open,
	onClose,
	content,
	jobId,
	setJobId,
	errorMessage,
	setErrorMessage,
	currentStep,
	setCurrentStep,
	selectedImage,
	setSelectedImage,
}) => {
	const { enqueueSnackbar } = useSnackbar();
	const { data: user } = useGetCurrentUserQuery();
	const { setJobIdToCheck } = useAiQualityTool();

	const aiRules = user?.preferences?.aiRules;
	const timePreferences = user?.preferences?.times;

	const [uploadScreenShot, { isLoading: isUploadingImage }] =
		useUploadCargoScreenShotMutation();

	const {
		data: jobStatus,
		isLoading: isLoadingStatus,
		isFetching: isFetchingStatus,
	} = useCheckJobStatusQuery(
		{ jobId },
		{
			skip: !jobId,
			pollingInterval: 3000, // 3s
		}
	);

	// handle errors
	useEffect(() => {
		if (errorMessage) {
			enqueueSnackbar(errorMessage, {
				variant: "error",
				preventDuplicate: true,
			});
		}
	}, [errorMessage]);

	const handleUpload = async () => {
		if (!selectedImage) return;

		try {
			// Convert base64 to Blob
			const blob = await fetch(selectedImage).then((res) => res.blob());

			// Build FormData for file upload
			const formData = new FormData();
			formData.append("screenshot", blob, "croppedScreenshot.png");

			const response = await uploadScreenShot({ data: formData }).unwrap();
			const { jobId } = response;

			if (jobId) {
				enqueueSnackbar(`Załadowano screenshot.`, { variant: "success" });
				setJobId(jobId);
				setCurrentStep(1);

				// quality tool
				setJobIdToCheck(jobId);
			}
		} catch (error) {
			if (error?.data?.errors) {
				const errors = error.data.errors;
				Object.entries(errors).forEach(([key, message]) => {
					const splittedKey = key.split(".");
					const parsedMessage = message[0].replace(key, splittedKey[2]);
					enqueueSnackbar(parsedMessage, {
						variant: "error",
						preventDuplicate: true,
					});
				});
			} else {
				enqueueSnackbar(error?.data.message || "Error uploading screenshot.", {
					variant: "error",
					preventDuplicate: true,
				});
			}
		}
	};

	const handleRemoveBox = (id) => {
		const filteredData = parsedResponse.filter((cargo) => cargo.id !== id);
		setParsedResponse(filteredData);
	};

	// track job status
	const [parsedResponse, setParsedResponse] = useState(null);
	useEffect(() => {
		if (!isLoadingStatus && !isFetchingStatus && jobStatus) {
			const { status } = jobStatus.data;

			if (status === "finished") {
				const parsed = JSON.parse(jobStatus.data.response);
				const parsedWithIds = parsed?.cargo_items.map((i, index) => {
					return { ...i, id: `id-${index}` };
				});
				const cleanedWithIds = parsedWithIds.filter((obj) =>
					Object.values(obj).some(
						(value) =>
							value !== "" &&
							value !== null &&
							value !== undefined &&
							value !== 0 &&
							!(Array.isArray(value) && value.length === 0)
					)
				);
				setParsedResponse(cleanedWithIds);
				setJobId(null);
				setCurrentStep(2);
			}

			if (status === "error") {
				setErrorMessage("Wystąpił błąd podczas analizy obrazu.");
				setJobId(null);
				setCurrentStep(0);
			}
		}
	}, [isLoadingStatus, isFetchingStatus]);

	// detect screen width
	const [screenWidth, setScreenWidth] = useState(window.innerWidth);
	const shouldHideDrawer = screenWidth < 1600;
	useEffect(() => {
		// detect resize
		const handleResize = () => setScreenWidth(window.innerWidth);

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const [drawerData, setDrawerData] = useState([]);
	useEffect(() => {
		if (parsedResponse?.length) {
			const data = prepareCargosData({
				cargos: parsedResponse,
				aiRules,
				timePreferences,
			});
			setDrawerData(data);
		} else {
			setDrawerData([]);
		}
	}, [aiRules, parsedResponse]);

	return (
		<Dialog
			fullWidth
			maxWidth={modalSizeDictionary[currentStep]}
			onClose={onClose}
			open={open}
			sx={{
				width:
					shouldHideDrawer || currentStep !== 2 ? "100%" : "calc(100% - 400px)",
			}}
			slotProps={{
				paper: {
					...(currentStep === 0 && {
						sx: {
							maxWidth: "90%",
							width: "auto",
						},
					}),
				},
			}}
		>
			{currentStep === 0 && (
				<IconButton
					aria-label="close"
					onClick={onClose}
					sx={{
						position: "absolute",
						right: 0,
						top: "-9px",
						color: "#ff5454",
					}}
				>
					<CloseIcon />
				</IconButton>
			)}
			{currentStep !== 0 && (
				<Button
					aria-label="close"
					variant="text"
					onClick={onClose}
					sx={{
						position: "absolute",
						right: 0,
						top: "-4px",
						color: "#ff5454",
					}}
				>
					Przerwij dodawanie
				</Button>
			)}
			{currentStep === 0 && (
				<EmailViewer
					content={content}
					selectedImage={selectedImage}
					setSelectedImage={setSelectedImage}
				/>
			)}
			{currentStep === 1 && <AnalyzeIndicator />}
			{currentStep === 2 && (
				<FormGrid
					onClose={onClose}
					shouldHideDrawer={shouldHideDrawer}
					drawerData={drawerData}
					handleRemoveBox={handleRemoveBox}
				/>
			)}
			{currentStep === 0 && (
				<DialogActions sx={{ p: "0 24px 16px 24px" }}>
					<Button
						variant="contained"
						disabled={!selectedImage}
						onClick={handleUpload}
						loading={isUploadingImage}
					>
						Dalej
					</Button>
				</DialogActions>
			)}
		</Dialog>
	);
};
